import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError } from '../../lib/api.js';
import { requestPromptDraft, type PromptDraft } from '../../lib/lesson-api.js';
import { t } from '../../lib/i18n.js';

/**
 * "Describe it in a sentence" — prompt-to-transaction (spec §13.3).
 *
 * It fills in the ordinary form below; it never saves anything. What it is
 * careful about:
 *
 *  - **It says what it did.** The fields it filled are the AI's reading of the
 *    sentence, and the panel says to check every one before saving.
 *  - **It asks rather than guesses.** Anything the sentence did not state, or
 *    that matched nothing the person may use, comes back as a question with a
 *    list to choose from — answered here, and applied to the form.
 *  - **It needs a signal.** Offline, the form works as always and this panel
 *    says why it is unavailable.
 */
export function PromptDraftPanel({
  lessonId,
  online,
  onApply,
  onAnswer,
  onDiscard,
}: {
  lessonId: string;
  online: boolean;
  onApply: (draft: PromptDraft) => void;
  onAnswer: (field: string, value: string) => void;
  onDiscard: () => void;
}) {
  const [prompt, setPrompt] = useState('');
  const [result, setResult] = useState<PromptDraft | null>(null);
  const [answered, setAnswered] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const ask = useMutation({
    mutationFn: () => requestPromptDraft(lessonId, prompt),
    onSuccess: (draft) => {
      setError(null);
      setResult(draft);
      setAnswered({});
      if (draft.suggestionId) onApply(draft);
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : t("The draft could not be made.")),
  });

  if (!online) {
    return (
      <p
        className="mt-6 rounded-md border border-border bg-surface p-4 text-sm text-content-secondary"
        data-testid="prompt-offline"
      >
        {t("Filling the form in from a sentence needs a signal. The form below works as usual.")}
      </p>
    );
  }

  const answer = (field: string, value: string) => {
    setAnswered({ ...answered, [field]: value });
    onAnswer(field, value);
  };

  return (
    <section className="mt-6 rounded-md border border-border bg-surface p-5" aria-labelledby="prompt-heading">
      <h2 id="prompt-heading" className="m-0 text-base font-semibold">
        {t("Describe it in a sentence")}
      </h2>
      <p className="mt-1 text-sm text-content-secondary">
        {t("AI fills in what your sentence states. You check every field before saving. Nothing is saved until you save.")}
      </p>
      <label className="sr-only" htmlFor="prompt-text">
        {t("Describe the lesson")}
      </label>
      <textarea
        id="prompt-text"
        rows={3}
        value={prompt}
        onChange={(event) => setPrompt(event.target.value)}
        placeholder={t("e.g. Formwork rework on Project Alpha — root cause was late drawing approval, moderate severity, negative impact.")}
        className="mt-3 w-full rounded-sm border border-border-strong px-3 py-2 text-sm"
      />
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={ask.isPending || prompt.trim().length < 10}
          onClick={() => ask.mutate()}
          className="rounded bg-ag-purple px-3 py-1.5 text-sm text-white disabled:opacity-60"
        >
          {ask.isPending ? t("Reading…") : t("Fill in the form")}
        </button>
        {result?.suggestionId && (
          <button
            type="button"
            onClick={() => {
              onDiscard();
              setResult(null);
            }}
            className="rounded border border-border px-3 py-1.5 text-sm"
          >
            {t("Discard AI draft")}
          </button>
        )}
      </div>

      {error && (
        <p role="alert" className="mb-0 mt-3 text-sm" style={{ color: 'var(--tone-rejected)' }}>
          {error}
        </p>
      )}
      {result && !result.suggestionId && (
        <p role="status" className="mb-0 mt-3 text-sm text-content-secondary">
          {result.unavailableReason ?? t("No draft this time. Fill the form in by hand.")}
        </p>
      )}

      {result?.suggestionId && (
        <div className="mt-3 rounded bg-tone-pending-surface p-3 text-sm" role="status" data-testid="prompt-applied">
          <p className="m-0">
            <strong>{t("AI draft applied below.")}</strong>{' '}{t("Check every field — it is the AI’s reading of your sentence, and you are the one who saves it.")}
          </p>
          {result.clarifications.length > 0 && (
            <ul className="m-0 mt-3 list-none space-y-2 p-0">
              {result.clarifications.map((question) => (
                <li key={question.field}>
                  <label className="flex flex-col gap-1">
                    <span>{question.question}</span>
                    {question.options ? (
                      <select
                        value={answered[question.field] ?? ''}
                        onChange={(event) => answer(question.field, event.target.value)}
                        className="rounded border border-border px-2 py-1"
                      >
                        <option value="">{t("Choose…")}</option>
                        {question.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {t(option.label)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={answered[question.field] ?? ''}
                        onChange={(event) => answer(question.field, event.target.value)}
                        className="rounded border border-border px-2 py-1"
                      />
                    )}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
